import { TweenMax } from 'gsap';
import * as _ from 'lodash';
import * as PIXI from 'pixi.js';

import SoundController from '@/assets/js/SoundController';
import Utils from '@/assets/js/Utils';
import GameManager from '@/assets/js/game/GameManager';
import FilterManager from '@/assets/js/game/FilterManager';
import MutekiAnimation from '@/assets/js/game/MutekiAnimation';
import TextureManager from '@/assets/js/game/TextureManager';

const PLAYER_WIDTH = 247.5;
const PLAYER_HEIGHT = 307;
const PLAYER_POSITION_X = 120;
const PLAYER_POSITION_Y = 640;

const HIT_AREA_WIDTH = 120;
const HIT_AREA_HEIGHT = 300;

const ABSORB_AREA_WIDTH = HIT_AREA_WIDTH * 8;
const ABSORB_AREA_HEIGHT = HIT_AREA_HEIGHT * 4;
const ABSORB_TIMER = 5;

const MUTEKI_TIMER = 5;

const JUMP_HEIGHT = 240;
const JUMP_DURATION = 0.5;
const TURN_JUMP_DURATION = JUMP_DURATION * 1.5;

const ANIMATE_SPEED = 0.1;

let animationList,
  damageFilter,
  mutekiFilter,
  sprite,
  container,
  hitArea,
  absorbArea,
  jumpAnimation,
  status;

export default class Player {
  static init(stage) {
    animationList = [
      {
        key: 'run',
        textureList: [
          TextureManager.get('player_run_1'),
          TextureManager.get('player_run_2'),
          TextureManager.get('player_run_3'),
          TextureManager.get('player_run_4')
        ]
      },
      {
        key: 'jump',
        textureList: [
          TextureManager.get('player_jump_1'),
          TextureManager.get('player_jump_2'),
          TextureManager.get('player_jump_3'),
          TextureManager.get('player_jump_4'),
          TextureManager.get('player_jump_5'),
          TextureManager.get('player_jump_6')
        ]
      },
      {
        key: 'turn',
        textureList: [
          TextureManager.get('player_turn_1'),
          TextureManager.get('player_turn_2'),
          TextureManager.get('player_turn_3'),
          TextureManager.get('player_turn_2'),
          TextureManager.get('player_turn_3'),
          TextureManager.get('player_turn_4'),
          TextureManager.get('player_turn_5'),
          TextureManager.get('player_turn_6'),
          TextureManager.get('player_turn_7')
        ]
      },
      {
        key: 'damage',
        textureList: [
          TextureManager.get('player_damage_1'),
          TextureManager.get('player_damage_2'),
          TextureManager.get('player_damage_3'),
          TextureManager.get('player_damage_4')
        ]
      }
    ];

    status = {
      absorbTimer: 0,
      mutekiTimer: 0,
      isAbsorb: false,
      isHit: false,
      isJump: false,
      isTurnJump: false,
      isMuteki: false
    };
    jumpAnimation = null;
    damageFilter = FilterManager.getDamageFilter();
    mutekiFilter = FilterManager.getMutekiFilter();

    sprite = new PIXI.AnimatedSprite(Player._getAnimation('run').textureList);
    sprite.animationSpeed = ANIMATE_SPEED;
    sprite.onComplete = () => Player._setAnimation('run', true);
    sprite.play();

    container = new PIXI.Container();

    // hitArea = new PIXI.Graphics();
    absorbArea = new PIXI.Graphics();
    hitArea = {};
    // absorbArea = {};
    Player._setSize();
    container.addChild(sprite);
    // container.addChild(hitArea);
    container.addChild(absorbArea);

    stage.addChild(container);
  }

  static reset() {
    status = {
      absorbTimer: 0,
      mutekiTimer: 0,
      isAbsorb: false,
      isHit: false,
      isJump: false,
      isTurnJump: false,
      isMuteki: false
    };
    jumpAnimation = null;
    Player._setSize();
  }

  static beforeDestroy() {
    sprite.onComplete = null;
  }

  static onResize() {
    Player._setSize();
  }

  static update(delta) {
    if (Player.isAbsorb) {
      status.absorbTimer += delta;
      if (Utils.msToSecond(status.absorbTimer) >= ABSORB_TIMER) {
        status.absorbTimer = 0;
        status.isAbsorb = false;
      }
    }

    if (Player.isMuteki) {
      status.mutekiTimer += delta;
      if (Utils.msToSecond(status.mutekiTimer) >= MUTEKI_TIMER) {
        status.mutekiTimer = 0;
        status.isMuteki = false;
        Player._clearFilter();
        MutekiAnimation.stop();
        SoundController.stop('jingle_muteki');
      }
    }
  }

  static pause() {
    if (jumpAnimation) {
      jumpAnimation.pause();
    }
  }

  static resume() {
    if (jumpAnimation) {
      jumpAnimation.resume();
    }
  }

  static standby() {
    TweenMax.to(sprite, {
      x: Player._getStaticPositionX(),
      duration: 1,
      ease: 'linear'
    });
  }

  static goal(onComplete) {
    const goalX = GameManager.calcRatio(GameManager.gameWidth);
    TweenMax.to(sprite, {
      x: `+=${goalX}`,
      duration: 1.4,
      ease: 'linear',
      onComplete: onComplete
    });
  }

  static jump() {
    if (status.isJump && status.isTurnJump) return;
    const moveHeight = GameManager.calcRatio(JUMP_HEIGHT);
    const finishY =
      GameManager.calcRatio(PLAYER_POSITION_Y) - sprite.height * 0.5;

    if (status.isJump && !status.isTurnJump) {
      jumpAnimation.pause();
      SoundController.play('se_player_turn_jump');
      Player._setAnimation('turn', false);
      status.isJump = true;
      status.isTurnJump = true;

      jumpAnimation = TweenMax.to([sprite, hitArea, absorbArea], {
        y: `-=${moveHeight}`,
        duration: JUMP_DURATION,
        ease: 'sine.out',
        onComplete: () => {
          jumpAnimation = TweenMax.to(sprite, {
            y: finishY,
            duration: TURN_JUMP_DURATION,
            ease: 'sine.in',
            onUpdate: () => {
              hitArea.y = absorbArea.y = sprite.y + sprite.height * 0.5;
            },
            onComplete: () => {
              status.isJump = false;
              status.isTurnJump = false;

              Player._setAnimation('run', true);
            }
          });
        }
      });
      return;
    }

    SoundController.play('se_player_jump');
    Player._setAnimation('jump', false);
    status.isJump = true;
    jumpAnimation = TweenMax.to([sprite, hitArea, absorbArea], {
      y: `-=${moveHeight}`,
      repeat: 1,
      yoyo: true,
      duration: JUMP_DURATION,
      onComplete: () => (status.isJump = false)
    });
  }

  static hit() {
    if (Player.isMuteki || Player.isHit) return;
    status.isHit = true;

    SoundController.play('se_player_damage');
    Player._setAnimation('damage', false);
    Player._setDamageAnimation(() => (status.isHit = false));
  }

  static absorb() {
    status.absorbTimer = 0;
    status.isAbsorb = true;
  }

  static muteki() {
    if (Player.isMuteki) return;
    status.isMuteki = true;
    status.mutekiTimer = 0;

    SoundController.play('jingle_muteki');

    sprite.filters = [mutekiFilter];
    MutekiAnimation.start();
  }

  static get centerX() {
    return sprite.x + sprite.width * 0.5;
  }

  static get centerY() {
    return sprite.y + sprite.height * 0.5;
  }

  static get hitArea() {
    return hitArea;
  }

  static get absorbArea() {
    return absorbArea;
  }

  static get isHit() {
    return status.isHit;
  }

  static get isAbsorb() {
    return status.isAbsorb;
  }

  static get isMuteki() {
    return status.isMuteki;
  }

  static _getStaticPositionX() {
    return GameManager.calcRatio(PLAYER_POSITION_X) - sprite.width * 0.5;
  }

  static _getAnimation(key) {
    return _.find(animationList, { key: key });
  }

  static _setAnimation(key, isLoop) {
    const data = Player._getAnimation(key);
    sprite.textures = data.textureList;
    sprite.animationSpeed = ANIMATE_SPEED;
    sprite.loop = isLoop;
    sprite.onComplete = () => Player._setAnimation('run', true);
    sprite.play();
  }

  static _setDamageAnimation(onComplete) {
    const flashCount = 5;
    const maxCount = flashCount * 2;
    let counter = 0;

    const flash = toggle => {
      if (toggle) {
        sprite.filters = [damageFilter];
      } else {
        Player._clearFilter();
      }
      counter += 1;
      if (counter > maxCount) {
        Player._clearFilter();
        onComplete();
        return;
      }
      setTimeout(() => flash(!toggle), 100);
    };
    flash(false);
  }

  static _setSize() {
    sprite.width = GameManager.calcRatio(PLAYER_WIDTH);
    sprite.height = GameManager.calcRatio(PLAYER_HEIGHT);
    sprite.x = -sprite.width;
    sprite.y = GameManager.calcRatio(PLAYER_POSITION_Y) - sprite.height * 0.5;

    hitArea.rx = GameManager.calcRatio(HIT_AREA_WIDTH * 0.5);
    hitArea.ry = GameManager.calcRatio(HIT_AREA_HEIGHT * 0.5);
    hitArea.x = Player._getStaticPositionX() + sprite.width * 0.5;
    hitArea.y = sprite.y + sprite.height * 0.5;
    // hitArea.alpha = 0.4;
    // hitArea
    //   .beginFill(0xff0000)
    //   .drawEllipse(0, 0, hitArea.rx, hitArea.ry)
    //   .endFill();

    absorbArea.rx = GameManager.calcRatio(ABSORB_AREA_WIDTH * 0.5);
    absorbArea.ry = GameManager.calcRatio(ABSORB_AREA_HEIGHT * 0.5);
    absorbArea.x = Player._getStaticPositionX() + sprite.width * 0.5;
    absorbArea.y = sprite.y + sprite.height * 0.5;
    // absorbArea.alpha = 0.4;
    // absorbArea
    //   .beginFill(0xff00)
    //   .drawEllipse(0, 0, absorbArea.rx, absorbArea.ry)
    //   .endFill();
  }

  static _clearFilter() {
    sprite.filters = [];
  }
}
